import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { Placeholder } from '../components/ProductCard.jsx';
import api from '../services/api.js';
import { money } from '../config/brand.js';
import { ik } from '../utils/imagekit.js';

const EMPTY = { name: '', email: '', phone: '', address: '', city: '', postal: '', notes: '' };

export default function Checkout() {
  const { cart, subtotal, shipping, total, clearCart, reload } = useStore();
  const notify = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  if (!cart.length) {
    return (
      <div className="page page--narrow center">
        <h1 className="page__title">Your bag is empty</h1>
        <p className="muted">Add something you love, then come back to check out.</p>
        <Link to="/shop" className="btn">Continue shopping</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const order = await api.placeOrder({
        customerName: form.name,
        customerEmail: form.email,
        customerPhone: form.phone,
        shippingAddress: [form.address, form.city, form.postal].filter(Boolean).join(', '),
        notes: form.notes,
        items: cart.map((l) => ({ id: l.id, size: l.size, quantity: l.quantity })),
      });
      clearCart();
      reload();
      navigate('/thank-you', { state: { order } });
    } catch (err) {
      setError(err.message);
      notify(err.message, 'error');
      reload(); // refresh stock so the bag matches what is available
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <h1 className="page__title">Checkout</h1>
      <div className="checkout">
        <form onSubmit={submit} className="form">
          <h2 className="form__title">Delivery details</h2>
          <label>Full name<input name="name" value={form.name} onChange={set} required autoComplete="name" /></label>
          <div className="form__row">
            <label>Email<input type="email" name="email" value={form.email} onChange={set} required autoComplete="email" /></label>
            <label>Phone<input type="tel" name="phone" value={form.phone} onChange={set} required autoComplete="tel" placeholder="03xx xxxxxxx" /></label>
          </div>
          <label>Address<input name="address" value={form.address} onChange={set} required autoComplete="street-address" /></label>
          <div className="form__row">
            <label>City<input name="city" value={form.city} onChange={set} required autoComplete="address-level2" /></label>
            <label>Postal code<input name="postal" value={form.postal} onChange={set} autoComplete="postal-code" /></label>
          </div>
          <label>Order notes (optional)<textarea name="notes" rows={3} value={form.notes} onChange={set} /></label>
          <h2 className="form__title">Payment</h2>
          <p className="muted">Cash on delivery. You pay when the order arrives.</p>
          {error && <p className="error" role="alert">{error}</p>}
          <button type="submit" className="btn btn--block" disabled={busy}>{busy ? 'Placing order' : `Place order  ${money(total)}`}</button>
        </form>

        <aside className="summary">
          <h2 className="form__title">Order summary</h2>
          {cart.map((l) => (
            <div key={l.key} className="line line--compact">
              <div className="line__img">{l.image ? <img src={ik(l.image, 160)} alt="" /> : <Placeholder name={l.name} />}</div>
              <div className="line__info">
                <p className="line__name">{l.name}</p>
                <p className="line__meta">{l.size ? `Size ${l.size} / ` : ''}Qty {l.quantity}</p>
              </div>
              <p className="line__total">{money(l.price * l.quantity)}</p>
            </div>
          ))}
          <div className="sum"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          <div className="sum sum--muted"><span>Shipping</span><span>{shipping ? money(shipping) : 'Free'}</span></div>
          <div className="sum sum--total"><span>Total</span><span>{money(total)}</span></div>
        </aside>
      </div>
    </div>
  );
}
